import { useFetcher } from 'thermia-react-container';
import { config } from '../config';
import { ApiKey, ApiKeySettings, CreateKeyRequest, Device, LockedError, User } from './api-types';

const rethrowLocked = (error: unknown): never => {
  if (error != null && typeof error === 'object' && 'status' in error && (error as { status: number }).status === 423) {
    throw new LockedError();
  }
  throw error;
};

const useApiClient = () => {
  const { fetcher } = useFetcher();

  const usersMe = async (): Promise<User> => {
    const url = `${config.apiUrl}/users/me`;
    const response = await fetcher<User>('GET', url).catch(rethrowLocked);
    return response;
  };

  const getKeys = async (): Promise<ApiKey[]> => {
    const url = `${config.apiUrl}/keys`;
    const response = await fetcher<ApiKey[]>('GET', url);
    return response;
  };

  const getKeySettings = async (): Promise<ApiKeySettings> => {
    const url = `${config.apiUrl}/keys/settings`;
    const response = await fetcher<ApiKeySettings>('GET', url);
    return response;
  };

  const createKey = async (request: CreateKeyRequest): Promise<ApiKey> => {
    const url = `${config.apiUrl}/keys`;
    return await fetcher<ApiKey>('POST', url, null, request);
  };

  const removeKey = async (id: string): Promise<void> => {
    const url = `${config.apiUrl}/keys/${id}`;
    await fetcher<void>('DELETE', url);
  };

  const getDevices = async (): Promise<Device[]> => {
    const url = `${config.apiUrl}/devices`;
    const response = await fetcher<Device[]>('GET', url);
    return response;
  };

  const showDeviceAccess = async (installationId: number): Promise<void> => {
    const url = `${config.apiUrl}/device-access/${installationId}/show`;
    await fetcher<void>('POST', url).catch(rethrowLocked);
  };

  const verifyDeviceAccess = async (installationId: number, verificationCode: string): Promise<boolean> => {
    const url = `${config.apiUrl}/device-access/${installationId}/verify/${verificationCode}`;
    return await fetcher<boolean>('POST', url).catch(rethrowLocked);
  };

  return {
    usersMe,
    getKeys,
    getKeySettings,
    createKey,
    removeKey,
    getDevices,
    showDeviceAccess,
    verifyDeviceAccess
  };
};

export default useApiClient;
